import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, TrendingUp, Smartphone, Check, ArrowRight, Star } from "lucide-react";
import { Section, Reveal, Eyebrow } from "@/components/primitives";
import { api } from "@/lib/apiClient";

const services = [
  {
    icon: Rocket,
    title: "Il tuo sito internet",
    desc: "Creiamo il sito della tua attività: chiaro, bello e facile da usare. Si vede bene sul computer e sul telefono. Prima di iniziare ti facciamo vedere una bozza gratis, così sai già come sarà.",
    includes: ["Bozza gratuita prima di iniziare", "Si vede bene su telefono e computer", "Le persone ti trovano su Google", "Le tue foto, i tuoi orari, i tuoi contatti", "Ti spieghiamo tutto con parole semplici"],
    time: "Bozza gratis · sito pronto in pochi giorni",
  },
  {
    icon: TrendingUp,
    title: "Farti trovare su Google",
    desc: "Quando una persona cerca un'attività come la tua, vogliamo che trovi te. Ci occupiamo della pubblicità online e di farti comparire nei risultati di ricerca.",
    includes: ["Pubblicità su Google e Facebook", "Comparire quando ti cercano", "Più visite e più telefonate", "Spendi solo quello che decidi tu", "Report semplici, senza paroloni"],
    time: "Si parte quando vuoi tu",
  },
  {
    icon: Smartphone,
    title: "I tuoi social",
    desc: "Gestiamo Facebook e Instagram al posto tuo: pubblichiamo foto e post, rispondiamo ai clienti e teniamo viva la tua pagina. Tu pensi al lavoro.",
    includes: ["Post pubblicati per te ogni mese", "Facebook e Instagram seguiti da noi", "Foto e contenuti curati", "Rispondiamo ai messaggi dei clienti", "Più persone che ti conoscono"],
    time: "Gestione mensile, sempre seguita",
  },
];

const plusModules = [
  "Più trovabile su Google", "Pubblicità online", "Foto e video professionali",
  "Logo e immagine coordinata", "Testi scritti per te", "Negozio online (e-commerce)",
  "Recensioni e Google Maps", "Email ai clienti", "Sempre al tuo fianco",
];

export default function Servizi() {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/packages").then((res) => setPackages(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="servizi-page">
      <Section className="pt-20 pb-12">
        <Reveal>
          <Eyebrow>Cosa facciamo</Eyebrow>
          <h1 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.05]">
            Il tuo sito internet, e molto altro.
          </h1>
          <p className="mt-6 text-lg text-ink/70 max-w-2xl">
            Partiamo dal sito della tua attività e ti aiutiamo a farti trovare
            online. Tutto spiegato in modo semplice, con soluzioni su misura per
            ogni azienda: dal professionista alla grande impresa.
          </p>
        </Reveal>
      </Section>

      {/* Service detail blocks */}
      <Section className="pb-12 space-y-8">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.05}>
            <div
              data-testid={`servizio-block-${i}`}
              className="bg-white rounded-2xl p-8 lg:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.04)] grid lg:grid-cols-3 gap-8"
            >
              <div>
                <div className="h-14 w-14 rounded-xl bg-coral/10 flex items-center justify-center text-coral">
                  <s.icon size={26} />
                </div>
                <h2 className="mt-5 font-display font-bold text-2xl text-ink">{s.title}</h2>
                <p className="mt-3 text-ink/60 leading-relaxed">{s.desc}</p>
                <span className="mt-5 inline-block text-sm font-semibold text-coral">{s.time}</span>
              </div>
              <div className="lg:col-span-2">
                <h3 className="text-sm uppercase tracking-widest text-ink/40 font-semibold">Cosa include</h3>
                <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                  {s.includes.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-ink/80">
                      <Check size={18} className="text-coral mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contatti"
                  data-testid={`servizio-cta-${i}`}
                  className="mt-7 inline-flex items-center gap-2 bg-ink text-beige rounded-full px-6 py-3 font-semibold hover:bg-black transition-colors"
                >
                  Richiedi info <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </Section>

      {/* Plus & Premium modules */}
      <div className="bg-ink text-beige">
        <Section className="py-20">
          <Reveal>
            <Eyebrow dark>Servizi in più</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-2xl">
              Quando vuoi fare un passo in più.
            </h2>
            <p className="mt-4 text-beige/60 max-w-xl">
              Aggiungi solo quello che ti serve davvero. Niente cose inutili, paghi solo ciò che usi.
            </p>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-3">
            {plusModules.map((m, i) => (
              <Reveal key={m} delay={i * 0.03}>
                <span className="inline-block border border-beige/20 rounded-full px-5 py-2.5 text-sm hover:border-coral hover:text-coral transition-colors">
                  {m}
                </span>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* Pricing comparison */}
      <Section className="py-24">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Pacchetti</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight text-ink">
              Scegli quello giusto per te.
            </h2>
            <p className="mt-4 text-ink/60">
              Prezzi chiari e trasparenti, su misura per ogni azienda. La bozza del sito è sempre gratis.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8 items-stretch">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div
                data-testid={`package-card-${p.name.toLowerCase()}`}
                className={`rounded-2xl p-8 h-full flex flex-col relative ${
                  p.highlighted
                    ? "bg-ink text-beige shadow-2xl scale-[1.02]"
                    : "bg-white text-ink shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
                }`}
              >
                {p.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1">
                    <Star size={12} /> Più scelto
                  </span>
                )}
                <h3 className="font-display font-bold text-2xl">{p.name}</h3>
                <p className={`mt-2 text-sm ${p.highlighted ? "text-beige/60" : "text-ink/50"}`}>{p.tagline}</p>
                <div className="mt-6">
                  {p.price ? (
                    <div className="font-display font-black text-4xl">
                      {p.price}
                      {p.period && <span className="text-base font-normal opacity-60"> /{p.period}</span>}
                    </div>
                  ) : (
                    <div className="font-display font-bold text-2xl text-coral">Su richiesta</div>
                  )}
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-coral mt-0.5 shrink-0" />
                      <span className={p.highlighted ? "text-beige/85" : "text-ink/75"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contatti"
                  data-testid={`package-cta-${p.name.toLowerCase()}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${
                    p.highlighted
                      ? "bg-coral text-white hover:bg-[#e04047]"
                      : "bg-ink text-beige hover:bg-black"
                  }`}
                >
                  Richiedi preventivo
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
}
